import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import React, { useCallback, useMemo, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { EMPTY_STRING } from "common/constants";
import VetExamOverlay from "components/VetExamOverlay/VetExamOverlay";
import DataAbsenceView from "components/VetExamsTable/DataAbsenceView/DataAbsenceView";
import VetExamsTable from "components/VetExamsTable/VetExamsTable";
import { IExamsTableRowData } from "components/VetExamsTable/types";
import { useRiskFactorsColumnWidth } from "components/VetExamsTable/useRiskFactorsColumnWidth";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { setSearchValue } from "store/actions/preRaceScreeningPage";
import { setIsExamFormOpen } from "store/actions/vetExam";
import { checkCurrentRaceEntriesTableTab, createConfigTable } from "../helper";
import {
  INFINITE_LIST_UPCOMING_RACE_ENTRIES_QUERY_KEY,
  PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY
} from "../options";
import { EPreRaceTableSortingField, EPreRacesTableTab } from "../types";
import PreRaceRow from "./PreRaceRow";
import PreRacesTableHeader from "./PreRacesTableHeader";

import useStyles from "./styles";

interface IProps {
  tableRows: IExamsTableRowData[];
  tableBodyRef: React.RefObject<HTMLDivElement>;
  isFetchingNextPage: boolean;
  isRefetching?: boolean;
}

const PreRacesTable = (props: IProps) => {
  const { tableBodyRef, isFetchingNextPage, isRefetching, tableRows } = props;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { selectedTab, searchValue, isExamFormOpen, orderBy } = useAppSelector(
    state => ({
      selectedTab: state.preRaceScreeningPage.selectedTab,
      searchValue: state.preRaceScreeningPage.searchValue,
      isExamFormOpen: state.vetExam.isExamFormOpen,
      orderBy: state.preRaceScreeningPage.orderBy
    }),
    shallowEqual
  );

  const [activeExamId, setActiveExamId] = useState<string>();

  const {
    isEntriesTab,
    isEntryExamsTab,
    isEntryResultsTab,
    isRaceDayTab,
    isRaceDayResultsTab
  } = checkCurrentRaceEntriesTableTab(selectedTab.value);

  const shouldRecalculateRiskFactorsWidth = [
    isEntriesTab,
    isEntryResultsTab,
    isRaceDayTab,
    isRaceDayResultsTab
  ].some(Boolean);
  const riskFactorsWidth = useRiskFactorsColumnWidth(
    tableRows,
    shouldRecalculateRiskFactorsWidth
  );
  const classes = useStyles({ riskFactorsWidth });

  const columns = useMemo(() => createConfigTable(selectedTab), [selectedTab]);
  const extraColumnsClassName = clsx({
    [classes.rowContentEntries]: isEntriesTab,
    [classes.rowContentEntryExams]: isEntryExamsTab,
    [classes.rowContentResults]: isEntryResultsTab,
    [classes.rowContentRaceDay]: isRaceDayTab,
    [classes.rowContentRaceDayResults]: isRaceDayResultsTab
  });

  const refetchData = useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: [INFINITE_LIST_UPCOMING_RACE_ENTRIES_QUERY_KEY]
    });
    queryClient.invalidateQueries({
      queryKey: [PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY]
    });
  }, [queryClient]);

  const handleCloseExamForm = useCallback(() => {
    dispatch(setIsExamFormOpen(false));
    setActiveExamId(null);
  }, [dispatch]);

  const handleOpenExamForm = useCallback(
    (examId: string, examStatus: racehorse360.HorseExamStatus) => {
      const isExamAvailable = [
        racehorse360.HorseExamStatus.HORSE_EXAM_STATUS_REQUESTED,
        racehorse360.HorseExamStatus.HORSE_EXAM_STATUS_IN_PROGRESS
      ].includes(examStatus);

      if (isExamAvailable) {
        setActiveExamId(examId);
        dispatch(setIsExamFormOpen(true));
      }
    },
    [dispatch]
  );

  const shouldRenderTrainerAccordions =
    orderBy === EPreRaceTableSortingField.TRAINER_LAST_NAME;

  const handleClearSearch = () => {
    dispatch(setSearchValue(EMPTY_STRING));
  };

  const noResultContent = useMemo(() => {
    let tabName: string;

    switch (selectedTab.value) {
      case EPreRacesTableTab.ENTRIES: {
        tabName = `Pre-Race ${selectedTab.name}`;
        break;
      }
      case EPreRacesTableTab.ENTRY_EXAMS: {
        tabName = selectedTab.name;
        break;
      }
      default: {
        tabName = `${selectedTab.name} Exams`;
      }
    }

    return (
      <DataAbsenceView
        isSearchResults={Boolean(searchValue?.length)}
        searchValue={searchValue}
        onClearSearch={handleClearSearch}
        message={`No ${tabName} to Display for Date Range`}
      />
    );
  }, [selectedTab.value, searchValue]);

  const tableHeaderContent = useMemo(
    () => (
      <PreRacesTableHeader
        columnsClassName={extraColumnsClassName}
        refElement={tableBodyRef.current}
        columns={columns}
      />
    ),
    [columns, extraColumnsClassName, tableBodyRef.current]
  );

  const renderRow = (rowData: IExamsTableRowData, index: number) => (
    <PreRaceRow
      index={index}
      currentRowData={rowData}
      columns={columns}
      columnsClassName={extraColumnsClassName}
      onOpenExamForm={handleOpenExamForm}
    />
  );

  if (isExamFormOpen && activeExamId) {
    return (
      <VetExamOverlay
        examId={activeExamId}
        refetchData={refetchData}
        onClose={handleCloseExamForm}
      />
    );
  }

  return (
    <VetExamsTable
      rows={tableRows}
      isMappedByTrainer={shouldRenderTrainerAccordions}
      isFetchingNextPage={isFetchingNextPage}
      isRefetching={isRefetching}
      tableBodyRef={tableBodyRef}
      noResultsContent={noResultContent}
      tableHeaderContent={tableHeaderContent}
      renderRow={renderRow}
      refetchData={refetchData}
      shouldUseLockableRow={isEntryExamsTab || isRaceDayTab}
      hasTrainerAccordionViewFixedCells={isEntryExamsTab || isRaceDayTab}
    />
  );
};

export default React.memo(PreRacesTable);
